import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { UserRole } from '../types/user';
import { useAuth } from './AuthProvider';

interface RoleContextType {
  activeRole: UserRole | null;
  setActiveRole: (role: UserRole) => void;
  availableRoles: UserRole[];
}

const RoleContext = createContext<RoleContextType | null>(null);

interface RoleProviderProps {
  children: ReactNode;
}

export const STORAGE_KEY = 'adu_active_role';

const getStorageKey = (userId?: string | null) =>
  userId ? `${STORAGE_KEY}_${userId}` : STORAGE_KEY;

export const RoleProvider: React.FC<RoleProviderProps> = ({ children }) => {
  const { user } = useAuth();

  const getPreferredRole = React.useCallback(
    (storedRole?: UserRole | null): UserRole | null => {
      if (!user) return null;
      if (user.roles.includes('ADMIN')) {
        const adminRoles = ['ADMIN', 'CONTRACTOR', 'USER'] as UserRole[];
        if (storedRole && adminRoles.includes(storedRole)) {
          return storedRole;
        }
        return 'ADMIN';
      }
      if (user.roles.includes('CONTRACTOR')) {
        return 'CONTRACTOR';
      }
      return (user.roles[0] as UserRole) || null;
    },
    [user]
  );

  // Calculate available roles immediately
  const availableRoles = React.useMemo(() => {
    if (!user) return [];
    if (user.roles.includes('ADMIN')) {
      return ['ADMIN', 'CONTRACTOR', 'USER'] as UserRole[];
    }
    return user.roles;
  }, [user]);

  const [activeRole, setActiveRoleState] = useState<UserRole | null>(() => {
    // Initialize synchronously from localStorage to prevent flicker
    if (!user) return null;

    const storedRole = localStorage.getItem(getStorageKey(user.id)) as UserRole | null;
    return getPreferredRole(storedRole);
  });

  useEffect(() => {
    if (!user) {
      setActiveRoleState(null);
      return;
    }

    const storedRole = localStorage.getItem(getStorageKey(user.id)) as UserRole | null;
    const preferredRole = getPreferredRole(storedRole);

    if (!preferredRole) {
      setActiveRoleState(null);
      return;
    }

    if (activeRole !== preferredRole) {
      setActiveRoleState(preferredRole);
      localStorage.setItem(getStorageKey(user.id), preferredRole);
    }
  }, [user, activeRole, getPreferredRole]);

  const setActiveRole = (role: UserRole) => {
    if (availableRoles.includes(role)) {
      setActiveRoleState(role);
      if (user) {
        localStorage.setItem(getStorageKey(user.id), role);
      }
    }
  };

  return (
    <RoleContext.Provider value={{ activeRole, setActiveRole, availableRoles }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useRole = (): RoleContextType => {
  const context = useContext(RoleContext);
  if (!context) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
};
